/**
 * Anlege-Route fuer addressbook.contacts (2026-07-15).
 *
 * Route: /admin/adressbuch/contacts/new
 * Query-Prefills:
 *   ?typus=firma|person       — Default: firma
 *   ?kategorie=kunde|ansprechpartner|lieferant|externer_handwerker — Default: kunde
 *   ?firma_id=<uuid>          — nur bei typus=person: praeselektiert die Firma
 *
 * Nach POST /adressbuch/contacts redirect auf /admin/adressbuch/contacts/:id.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, User as UserIcon, Loader2, Save, X, Truck, Wrench, Search } from 'lucide-react';
import { api, useAuth } from '@onvice/portal-shell-api';

type Typus = 'firma' | 'person';
type Kategorie = 'kunde' | 'ansprechpartner' | 'lieferant' | 'externer_handwerker';

const KATEGORIE_LABEL: Record<Kategorie, string> = {
  kunde: 'Kunde',
  ansprechpartner: 'Ansprechpartner',
  lieferant: 'Lieferant',
  externer_handwerker: 'Ext. Handwerker',
};

const KATEGORIE_ICON: Record<Kategorie, typeof Building2> = {
  kunde: Building2,
  ansprechpartner: UserIcon,
  lieferant: Truck,
  externer_handwerker: Wrench,
};

interface FirmaOption {
  id: string;
  firma_name: string | null;
  plz: string | null;
  ort: string | null;
}

interface Form {
  typus: Typus;
  kategorien: Kategorie[];
  firma_id: string | null;
  firma_name: string;
  kuerzel: string;
  ust_id: string;
  hr_eintrag: string;
  bank_iban: string;
  zahlungsziel_tage: string;
  anrede: string;
  titel: string;
  vorname: string;
  nachname: string;
  position: string;
  strasse: string;
  hausnummer: string;
  plz: string;
  ort: string;
  land: string;
  telefon: string;
  mobil: string;
  email: string;
  notizen: string;
}

const emptyForm: Form = {
  typus: 'firma', kategorien: ['kunde'], firma_id: null,
  firma_name: '', kuerzel: '', ust_id: '', hr_eintrag: '', bank_iban: '', zahlungsziel_tage: '',
  anrede: '', titel: '', vorname: '', nachname: '', position: '',
  strasse: '', hausnummer: '', plz: '', ort: '', land: '',
  telefon: '', mobil: '', email: '', notizen: '',
};

function parseTypus(v: string | null): Typus {
  return v === 'person' ? 'person' : 'firma';
}
function parseKategorie(v: string | null): Kategorie {
  if (v === 'ansprechpartner' || v === 'lieferant' || v === 'externer_handwerker') return v;
  return 'kunde';
}

export default function AdressbuchContactNeu() {
  const { isAdmin, hasPermission } = useAuth();
  // SD-2026-00021 v3 (Onvice, 2026-08-01): Permission-basiert statt Rollen (admin/gf/editor).
  // adressbuch.write kommt via MODULE_PERMISSION_MAPPING aus der Adressbuch-Gruppe des Users
  // (Buero/GF haben editor+ auf module_access.adressbuch → Permission).
  const canEdit = isAdmin || hasPermission('adressbuch.write');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialTypus = parseTypus(searchParams.get('typus'));
  const initialKategorie = parseKategorie(searchParams.get('kategorie'));
  const initialFirmaId = searchParams.get('firma_id');

  const [form, setForm] = useState<Form>({
    ...emptyForm,
    typus: initialTypus,
    kategorien: [initialKategorie],
    firma_id: initialTypus === 'person' ? initialFirmaId : null,
  });
  const [prefilledFirma, setPrefilledFirma] = useState<FirmaOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Vorbelegte Firma-Details laden (aus firma_id im URL-Param)
  useEffect(() => {
    if (form.typus !== 'person' || !form.firma_id) { setPrefilledFirma(null); return; }
    api.get<{ success: boolean; data: FirmaOption }>(`/adressbuch/contacts/${form.firma_id}`)
      .then((r) => setPrefilledFirma(r.data))
      .catch(() => setPrefilledFirma(null));
  }, [form.typus, form.firma_id]);

  function setField<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleKategorie(k: Kategorie) {
    setForm((f) => {
      const has = f.kategorien.includes(k);
      const next = has ? f.kategorien.filter((x) => x !== k) : [...f.kategorien, k];
      return { ...f, kategorien: next.length === 0 ? [k] : next };
    });
  }

  function switchTypus(t: Typus) {
    setForm((f) => ({ ...f, typus: t, firma_id: t === 'person' ? f.firma_id : null }));
  }

  const canSave = useMemo(() => {
    if (form.kategorien.length === 0) return false;
    if (form.typus === 'firma') return form.firma_name.trim().length > 0;
    return form.nachname.trim().length > 0;
  }, [form]);

  async function save() {
    if (!canSave || saving) return;
    setSaving(true); setError(null);
    try {
      const zahlungsziel = form.zahlungsziel_tage.trim();
      const body: Record<string, unknown> = {
        typus: form.typus,
        kategorien: form.kategorien,
        firma_id: form.typus === 'person' ? form.firma_id : null,
        firma_name: form.typus === 'firma' ? form.firma_name.trim() : null,
        kuerzel: form.kuerzel.trim() || null,
        ust_id: form.ust_id.trim() || null,
        hr_eintrag: form.hr_eintrag.trim() || null,
        bank_iban: form.bank_iban.trim() || null,
        zahlungsziel_tage: zahlungsziel ? Number(zahlungsziel) : null,
        anrede: form.anrede.trim() || null,
        titel: form.titel.trim() || null,
        vorname: form.vorname.trim() || null,
        nachname: form.typus === 'person' ? form.nachname.trim() : null,
        position: form.position.trim() || null,
        strasse: form.strasse.trim() || null,
        hausnummer: form.hausnummer.trim() || null,
        plz: form.plz.trim() || null,
        ort: form.ort.trim() || null,
        land: form.land.trim() || null,
        telefon: form.telefon.trim() || null,
        mobil: form.mobil.trim() || null,
        email: form.email.trim() || null,
        notizen: form.notizen.trim() || null,
      };
      const r = await api.post<{ success: boolean; data: { id: string } }>('/adressbuch/contacts', body);
      const newId = r.data?.id;
      if (newId) {
        navigate(`/admin/adressbuch/contacts/${newId}`, { replace: true });
      } else {
        navigate('/admin/adressbuch?tab=contacts', { replace: true });
      }
    } catch (e: unknown) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  if (!canEdit) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Link to="/admin/adressbuch?tab=contacts" className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-4">
          <ArrowLeft size={14} /> Adressbuch
        </Link>
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-300">
          Nur Admin oder GF darf Kontakte anlegen.
        </div>
      </div>
    );
  }

  const backTo = `/admin/adressbuch?tab=contacts${initialKategorie !== 'kunde' ? `&kategorie=${initialKategorie}` : ''}`;

  return (
    <div className="min-h-screen">
      {/* Sticky Header */}
      <div className="sticky top-14 z-20 bg-card/95 backdrop-blur border-b border-default">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to={backTo} className="text-slate-400 hover:text-slate-200 p-1">
            <ArrowLeft size={18} />
          </Link>
          <div className="text-2xl flex-shrink-0">{form.typus === 'firma' ? '🏢' : '👤'}</div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-foreground truncate">Neuer Kontakt</h1>
            <div className="text-xs text-muted-foreground">
              {form.typus === 'firma' ? 'Firma' : 'Person'}
              {form.kategorien.length > 0 && ' · ' + form.kategorien.map((k) => KATEGORIE_LABEL[k]).join(', ')}
            </div>
          </div>
          <Link to={backTo} className="inline-flex items-center gap-1 px-3 py-2 rounded border border-slate-700 text-slate-300 text-sm hover:bg-slate-800">
            <X size={14} /> Abbrechen
          </Link>
          <button
            onClick={() => void save()}
            disabled={!canSave || saving}
            className="inline-flex items-center gap-1 px-3 py-2 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {error && <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300">{error}</div>}

        {/* Typus-Umschalter */}
        <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Typ</h2>
          <div className="inline-flex rounded-md border border-slate-800 bg-slate-900/50 p-0.5">
            <button
              type="button"
              onClick={() => switchTypus('firma')}
              className={`px-4 py-1.5 rounded text-sm transition-colors ${
                form.typus === 'firma' ? 'bg-brand-600 text-primary-foreground' : 'text-slate-400 hover:bg-slate-800'
              }`}
            >
              🏢 Firma
            </button>
            <button
              type="button"
              onClick={() => switchTypus('person')}
              className={`px-4 py-1.5 rounded text-sm transition-colors ${
                form.typus === 'person' ? 'bg-brand-600 text-primary-foreground' : 'text-slate-400 hover:bg-slate-800'
              }`}
            >
              👤 Person
            </button>
          </div>
        </section>

        {/* Kategorien */}
        <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-3">
          <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Kategorien</h2>
          <p className="text-xs text-slate-500">Mindestens eine Kategorie. Mehrfach-Zuordnung erlaubt (z.B. Kunde + Lieferant).</p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(KATEGORIE_LABEL) as Kategorie[]).map((k) => {
              const Icon = KATEGORIE_ICON[k];
              const active = form.kategorien.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleKategorie(k)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    active
                      ? 'bg-brand-500/20 text-brand-200 border-brand-500/50'
                      : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <Icon size={12} /> {KATEGORIE_LABEL[k]}
                </button>
              );
            })}
          </div>
        </section>

        {/* Firma-Zuordnung (nur bei typus=person + Kategorie Ansprechpartner) */}
        {form.typus === 'person' && (
          <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-3">
            <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider flex items-center gap-2">
              <Building2 size={14} /> Firma-Zuordnung
              <span className="text-xs font-normal text-slate-500 normal-case tracking-normal">(optional — pflicht bei Ansprechpartner)</span>
            </h2>
            {form.firma_id && prefilledFirma ? (
              <div className="flex items-center justify-between rounded border border-slate-700 bg-slate-800/60 px-3 py-2">
                <div className="text-sm text-slate-100">
                  🏢 <span className="font-medium">{prefilledFirma.firma_name ?? '(unbenannt)'}</span>
                  {(prefilledFirma.plz || prefilledFirma.ort) && (
                    <span className="text-xs text-slate-400 ml-2">{prefilledFirma.plz} {prefilledFirma.ort}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setField('firma_id', null)}
                  className="text-slate-400 hover:text-rose-400 text-xs"
                  title="Firma-Zuordnung entfernen"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <FirmaSearchSelect onSelect={(f) => { setField('firma_id', f.id); setPrefilledFirma(f); }} />
            )}
          </section>
        )}

        {/* Basis-Felder */}
        <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">
            {form.typus === 'firma' ? 'Firma-Stammdaten' : 'Person-Stammdaten'}
          </h2>
          {form.typus === 'firma' ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Firmenname *" value={form.firma_name} onChange={(v) => setField('firma_name', v)} className="sm:col-span-2" required />
              <Field label="Kuerzel" value={form.kuerzel} onChange={(v) => setField('kuerzel', v)} monospace placeholder="z.B. MEIER" />
              <Field label="USt-IdNr." value={form.ust_id} onChange={(v) => setField('ust_id', v)} monospace placeholder="DE123456789" />
              <Field label="HR-Eintrag" value={form.hr_eintrag} onChange={(v) => setField('hr_eintrag', v)} placeholder="HRB 12345 Berlin" />
              <Field label="Zahlungsziel (Tage)" value={form.zahlungsziel_tage} onChange={(v) => setField('zahlungsziel_tage', v.replace(/[^0-9]/g, ''))} placeholder="14" />
              <Field label="IBAN" value={form.bank_iban} onChange={(v) => setField('bank_iban', v)} monospace placeholder="DE89 3704 …" className="sm:col-span-3" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Anrede" value={form.anrede} onChange={(v) => setField('anrede', v)} placeholder="Herr / Frau" />
              <Field label="Titel" value={form.titel} onChange={(v) => setField('titel', v)} placeholder="Dr., Prof." />
              <div />
              <Field label="Vorname" value={form.vorname} onChange={(v) => setField('vorname', v)} />
              <Field label="Nachname *" value={form.nachname} onChange={(v) => setField('nachname', v)} required />
              <Field label="Position" value={form.position} onChange={(v) => setField('position', v)} placeholder="z.B. Einkauf" />
            </div>
          )}
        </section>

        {/* Adresse */}
        <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Adresse</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Field label="Strasse" value={form.strasse} onChange={(v) => setField('strasse', v)} className="sm:col-span-3" />
            <Field label="Hausnummer" value={form.hausnummer} onChange={(v) => setField('hausnummer', v)} />
            <Field label="PLZ" value={form.plz} onChange={(v) => setField('plz', v)} />
            <Field label="Ort" value={form.ort} onChange={(v) => setField('ort', v)} className="sm:col-span-2" />
            <Field label="Land" value={form.land} onChange={(v) => setField('land', v)} placeholder="Deutschland" />
          </div>
        </section>

        {/* Kontakt */}
        <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-4">
          <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Kontakt</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Telefon" value={form.telefon} onChange={(v) => setField('telefon', v)} type="tel" />
            <Field label="Mobil" value={form.mobil} onChange={(v) => setField('mobil', v)} type="tel" />
            <Field label="E-Mail" value={form.email} onChange={(v) => setField('email', v)} type="email" />
          </div>
        </section>

        {/* Notizen */}
        <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 space-y-3">
          <h2 className="text-sm font-semibold text-slate-100 uppercase tracking-wider">Notizen</h2>
          <textarea
            value={form.notizen}
            onChange={(e) => setField('notizen', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm"
            placeholder="Interne Notizen zum Kontakt (Zahlungshistorie, Besonderheiten, ...)"
          />
        </section>

        {/* Wiederholte Aktions-Buttons unten fuer lange Formulare */}
        <div className="flex justify-end gap-2 pb-8">
          <Link to={backTo} className="inline-flex items-center gap-1 px-4 py-2 rounded border border-slate-700 text-slate-300 text-sm hover:bg-slate-800">
            <X size={14} /> Abbrechen
          </Link>
          <button
            onClick={() => void save()}
            disabled={!canSave || saving}
            className="inline-flex items-center gap-1 px-4 py-2 rounded bg-brand-600 hover:bg-brand-500 text-primary-foreground text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Kontakt anlegen
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder, className, monospace, required }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; className?: string; monospace?: boolean; required?: boolean;
}) {
  return (
    <div className={className}>
      <label className="block text-xs text-slate-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={`w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm ${monospace ? 'font-mono' : ''}`}
      />
    </div>
  );
}

/** Duenner Search-Select fuer Firma-Kontakte. Debounce 300ms, top 10. */
function FirmaSearchSelect({ onSelect }: { onSelect: (f: FirmaOption) => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<FirmaOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); return; }
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get<{ success: boolean; data: FirmaOption[] }>(`/adressbuch/contacts?typus=firma&q=${encodeURIComponent(term)}&limit=10`);
        setResults(r.data ?? []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [q]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Firma suchen (min. 2 Zeichen)…"
            className="w-full pl-9 pr-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-sm"
          />
        </div>
        {loading && <Loader2 size={14} className="animate-spin text-slate-500" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 shadow-lg max-h-60 overflow-y-auto">
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => { onSelect(r); setQ(''); setResults([]); setOpen(false); }}
              className="block w-full text-left px-3 py-2 hover:bg-slate-800 text-sm text-slate-100"
            >
              🏢 <span className="font-medium">{r.firma_name ?? '(unbenannt)'}</span>
              {(r.plz || r.ort) && <span className="text-xs text-slate-400 ml-2">{r.plz} {r.ort}</span>}
            </button>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && !loading && results.length === 0 && open && (
        <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 shadow-lg px-3 py-2 text-xs text-slate-500">
          Keine Firma gefunden. Erst als Firma-Kontakt anlegen, dann Ansprechpartner zuordnen.
        </div>
      )}
    </div>
  );
}
